import { GatewayRpcError } from '@modules/equabot-settings/services/gateway-rpc'

import type { AdminRpcErrorReport } from '../types/admin'

type Reporter = (report: AdminRpcErrorReport) => void

const defaultReporter: Reporter = (report) => {
  // Fallback until a product-wide error pipeline is registered.
  // Secrets are not part of the report by construction.
  // eslint-disable-next-line no-console
  console.error('[equabot-admin rpc error]', report)
}

let reporter: Reporter = defaultReporter

/**
 * Replace the default reporter with a product-wide error sink.
 * Call once during app bootstrap (e.g. alongside other telemetry wiring).
 */
export function registerRpcErrorReporter(next: Reporter): () => void {
  const prev = reporter
  reporter = next
  return () => { reporter = prev }
}

let correlationCounter = 1
function nextCorrelationId(): string {
  return `admin-rpc-${Date.now()}-${correlationCounter++}`
}

/** Extract top-level keys of a params object without exposing values. */
function shapeOf(params: unknown): string[] {
  if (!params || typeof params !== 'object') return []
  return Object.keys(params as Record<string, unknown>).sort()
}

export type RpcErrorContext = {
  method: string
  params?: unknown
  organizationId: string | null
}

/**
 * Normalize and report an RPC error per NFR-004. Returns the correlation id so
 * callers can surface it in UI toasts / logs.
 */
export function reportRpcError(err: unknown, ctx: RpcErrorContext): string {
  const correlationId = nextCorrelationId()
  const code = err instanceof GatewayRpcError ? err.code : 'UNKNOWN'
  const message = err instanceof Error ? err.message : String(err)
  const report: AdminRpcErrorReport = {
    method: ctx.method,
    argsShape: shapeOf(ctx.params),
    code,
    message,
    correlationId,
    organizationId: ctx.organizationId,
  }
  try { reporter(report) } catch { /* reporter must not throw */ }
  return correlationId
}
